import { useState, useEffect } from 'react';
import { useTheme } from '../../hooks/useTheme';
import { useCartStore } from '../../store/cart.store';
import { useFavoritesStore } from '../../store/favorites.store';
import { ProductsService } from '../../services/products/products.service';
import type { ProductSearchResult } from '../../services/products/products.service';
import { useProductFilters, getPrecioPrincipal, getStockPrincipal } from '../../hooks/useProductFilters';
import { ProductCard } from '../../components/products/ProductCard';
import { getFirstProductImage } from '../../utils/product-images';
import { capitalizeProductName } from '../../utils/capitalize';
import {
  Search,
  Filter,
  Grid,
  List,
  ShoppingCart,
  Heart,
  Tag,
  Package,
  ChevronDown,
  Pill,
  Baby,
  Activity,
  Sparkles,
  Shield,
  Stethoscope,
  Droplets,
  X,
  SlidersHorizontal,
} from 'lucide-react';
import toast from 'react-hot-toast';

const categorias = [
  { id: '', nombre: 'Todos', icono: null },
  { id: 'medicamentos', nombre: 'Medicamentos', icono: <Pill className="w-4 h-4" /> },
  { id: 'cuidado-personal', nombre: 'Cuidado Personal', icono: <Sparkles className="w-4 h-4" /> },
  { id: 'higiene-femenina', nombre: 'Higiene Femenina', icono: <Droplets className="w-4 h-4" /> },
  { id: 'salud-sexual', nombre: 'Salud Sexual', icono: <Heart className="w-4 h-4" /> },
  { id: 'vitaminas-y-suplementos', nombre: 'Vitaminas y Suplementos', icono: <Activity className="w-4 h-4" /> },
  { id: 'bebes', nombre: 'Bebés', icono: <Baby className="w-4 h-4" /> },
  { id: 'equipos-medicos', nombre: 'Equipos Médicos', icono: <Stethoscope className="w-4 h-4" /> },
];

const ordenOpciones = [
  { value: 'relevancia', label: 'Relevancia' },
  { value: 'precio_asc', label: 'Precio: menor a mayor' },
  { value: 'precio_desc', label: 'Precio: mayor a menor' },
  { value: 'nombre', label: 'Nombre A-Z' },
  { value: 'mas_vendidos', label: 'Más vendidos' },
];

export const ComprasClientePage: React.FC = () => {
  const { theme } = useTheme();
  const { addItem, items, isOpen: cartOpen } = useCartStore();
  const { toggleFavorite, isFavorite } = useFavoritesStore();
  const [productos, setProductos] = useState<ProductSearchResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [vistaGrid, setVistaGrid] = useState(true);
  const [addedItems, setAddedItems] = useState<string[]>([]);
  const [dropdownOrdenAbierto, setDropdownOrdenAbierto] = useState(false);
  const [mostrados, setMostrados] = useState(10);

  const {
    busqueda,
    categoria,
    precioMax,
    soloDisponibles,
    orden,
    setBusqueda,
    setCategoria,
    setPrecioMax,
    setSoloDisponibles,
    setOrden,
    contarActivos,
    reset,
    filtrar,
    ordenar,
  } = useProductFilters();

  // Cargar productos
  useEffect(() => {
    const cargarProductos = async () => {
      try {
        setLoading(true);
        const response = await ProductsService.getPublic(1000);
        if (response.success) {
          setProductos(response.data);
        }
      } catch (error) {
        console.error('Error al cargar productos:', error);
        toast.error('Error al cargar los productos');
      } finally {
        setLoading(false);
      }
    };
    cargarProductos();
  }, []);

  const productosFiltrados = ordenar(filtrar(productos));

  const getItemQuantity = (productId: string) => {
    const item = items.find(item => item.id === productId);
    return item ? item.cantidad : 0;
  };

  const handleAddToCart = (producto: ProductSearchResult, presId: string, presNombre: string) => {
    const pres = producto.presentaciones?.find(p => p.id === presId);
    addItem({
      id: producto.id,
      nombre: producto.nombre,
      precio: pres?.precio || 0,
      precioAntes: pres?.precioAntes,
      imagen: getFirstProductImage(producto.imagenes),
      receta: producto.requiereReceta,
      categoria: producto.categoria,
      presentacionId: presId,
      presentacionNombre: presNombre,
    });
    toast.success(`${producto.nombre} agregado al carrito`);
    setAddedItems(prev => [...prev, producto.id]);
    setTimeout(() => {
      setAddedItems(prev => prev.filter(id => id !== producto.id));
    }, 1500);
  };

  const ProductListItem = ({ producto }: { producto: ProductSearchResult }) => {
    const cantidadEnCarrito = getItemQuantity(producto.id);
    const pres = producto.presentaciones?.[0];

    return (
      <div className={`border rounded-xl overflow-hidden transition-all duration-300 ${
        theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <div className="p-4 sm:p-6">
          <div className="flex items-start space-x-4 sm:space-x-6">
            {/* Imagen */}
            <div className={`w-20 h-20 sm:w-24 sm:h-24 rounded-lg overflow-hidden flex-shrink-0 bg-white flex items-center justify-center`}>
              {getFirstProductImage(producto.imagenes) ? (
                <img
                  src={getFirstProductImage(producto.imagenes)}
                  alt={producto.nombre}
                  className="w-full h-full object-contain"
                />
              ) : (
                <Package className="w-8 h-8 text-gray-400" />
              )}
            </div>

            {/* Información */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="min-w-0">
                  <h3 className={`font-semibold text-base sm:text-lg truncate ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {capitalizeProductName(producto.nombre)}
                  </h3>
                  {producto.variante && (
                    <p className={`text-xs mt-0.5 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                      {producto.variante}
                    </p>
                  )}
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <div className="flex items-center space-x-1">
                      <Tag className="w-3.5 h-3.5 text-gray-400" />
                      <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        {producto.categoria}
                      </span>
                    </div>
                    {producto.requiereReceta && (
                      <span className="px-2 py-0.5 bg-red-100 text-red-800 text-xs rounded-full">
                        Requiere receta
                      </span>
                    )}
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      getStockPrincipal(producto) > 0
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {getStockPrincipal(producto) > 0 ? `Stock: ${getStockPrincipal(producto)}` : 'Agotado'}
                    </span>
                  </div>
                  {/* Presentaciones en list view */}
                  {producto.presentaciones && producto.presentaciones.length > 1 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {producto.presentaciones.map(pres => (
                        <span
                          key={pres.id}
                          className={`text-xs px-2 py-0.5 rounded-full border ${
                            theme === 'dark'
                              ? 'border-gray-600 text-gray-300 bg-gray-700'
                              : 'border-gray-200 text-gray-600 bg-gray-50'
                          }`}
                        >
                          {pres.nombre}: ${pres.precio.toLocaleString('es-CO')}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Precio y acciones */}
                <div className="text-right flex-shrink-0">
                  <p className={`text-xl sm:text-2xl font-bold ${theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'}`}>
                    ${getPrecioPrincipal(producto).toLocaleString('es-CO')}
                  </p>
                  {producto.presentaciones?.[0]?.precioAntes && (
                    <p className="text-sm text-gray-400 line-through">
                      ${producto.presentaciones[0].precioAntes.toLocaleString('es-CO')}
                    </p>
                  )}

                  <div className="flex items-center justify-end space-x-2 mt-3">
                    <button
                      onClick={() => toggleFavorite(producto)}
                      className={`p-2 rounded-lg border transition-colors ${
                        isFavorite(producto.id)
                          ? 'bg-red-50 border-red-200 text-red-500'
                          : theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-gray-300 hover:text-red-400'
                            : 'bg-white border-gray-200 text-gray-600 hover:text-red-500'
                      }`}
                      title={isFavorite(producto.id) ? 'Quitar de favoritos' : 'Agregar a favoritos'}
                    >
                      <Heart className={`w-4 h-4 ${isFavorite(producto.id) ? 'fill-red-500' : ''}`} />
                    </button>

                    {cantidadEnCarrito > 0 && (
                      <div className={`flex items-center space-x-2 px-3 py-1 rounded-lg ${
                        theme === 'dark' ? 'bg-emerald-900/50 text-emerald-300' : 'bg-emerald-100 text-emerald-700'
                      }`}>
                        <span className="text-sm font-medium">{cantidadEnCarrito}</span>
                      </div>
                    )}

                    {getStockPrincipal(producto) > 0 && pres ? (
                      <button
                        onClick={() => handleAddToCart(producto, pres.id, pres.nombre)}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                          theme === 'dark'
                            ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                            : 'bg-emerald-500 hover:bg-emerald-600 text-white'
                        }`}
                      >
                        <ShoppingCart className="w-4 h-4" />
                        <span>Agregar</span>
                      </button>
                    ) : (
                      <button
                        disabled
                        className="px-4 py-2 rounded-lg font-medium bg-gray-300 text-gray-500 cursor-not-allowed"
                      >
                        Agotado
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`min-h-screen transition-all duration-300 ${
      theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
    } ${cartOpen ? 'lg:pr-[420px]' : ''}`}>
      {/* Header */}
      <div className={`w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 border-b ${
        theme === 'dark'
          ? 'bg-gray-900 border-gray-800'
          : 'bg-gray-50 border-gray-200'
      }`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4">
          <div>
            <h1 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Catálogo de Productos
            </h1>
            <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              Encuentra los mejores productos para tu salud y bienestar
            </p>
          </div>
          <div className={`px-4 py-2 rounded-full text-sm font-medium ${
            theme === 'dark' ? 'bg-emerald-900/50 text-emerald-300' : 'bg-emerald-50 text-emerald-700'
          }`}>
            {productosFiltrados.length} producto{productosFiltrados.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Barra de búsqueda y controles */}
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar medicamentos, productos..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className={`w-full pl-12 pr-4 py-3 rounded-xl border-2 transition-all duration-300 ${
                theme === 'dark'
                  ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20'
                  : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20'
              }`}
            />
            {busqueda && (
              <button
                onClick={() => setBusqueda('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Ordenar */}
            <div className="relative">
              <button
                onClick={() => setDropdownOrdenAbierto(!dropdownOrdenAbierto)}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl border-2 text-sm font-medium transition-all min-w-[180px] justify-between ${
                  theme === 'dark'
                    ? 'bg-gray-800 border-gray-700 text-white hover:border-emerald-500'
                    : 'bg-white border-gray-200 text-gray-900 hover:border-emerald-500'
                }`}
              >
                <span className="truncate">{ordenOpciones.find(o => o.value === orden)?.label}</span>
                <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${dropdownOrdenAbierto ? 'rotate-180' : ''}`} />
              </button>
              {dropdownOrdenAbierto && (
                <div className={`absolute right-0 mt-1 w-full min-w-[200px] border rounded-xl shadow-lg z-50 overflow-hidden ${
                  theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}>
                  {ordenOpciones.map((opcion) => (
                    <button
                      key={opcion.value}
                      onClick={() => { setOrden(opcion.value as any); setDropdownOrdenAbierto(false); }}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                        orden === opcion.value
                          ? theme === 'dark'
                            ? 'bg-emerald-900/30 text-emerald-400 font-medium'
                            : 'bg-emerald-50 text-emerald-700 font-medium'
                          : theme === 'dark'
                            ? 'text-gray-300 hover:bg-gray-700'
                            : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {opcion.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Filtros toggle mobile */}
            <button
              onClick={() => setMostrarFiltros(!mostrarFiltros)}
              className={`relative flex items-center gap-2 px-4 py-3 rounded-xl border-2 transition-all ${
                mostrarFiltros
                  ? 'border-emerald-500 text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20 dark:text-emerald-400'
                  : theme === 'dark'
                    ? 'border-gray-700 text-gray-300 hover:bg-gray-800'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <SlidersHorizontal className="w-5 h-5" />
              <span className="hidden sm:inline text-sm font-medium">Filtros</span>
              {contarActivos > 0 && (
                <span className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {contarActivos}
                </span>
              )}
            </button>

            {/* Vista grid/list */}
            <div className={`flex items-center border-2 rounded-xl p-1 ${
              theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <button
                onClick={() => setVistaGrid(true)}
                className={`p-2 rounded-lg transition-colors ${
                  vistaGrid
                    ? 'bg-emerald-500 text-white'
                    : theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setVistaGrid(false)}
                className={`p-2 rounded-lg transition-colors ${
                  !vistaGrid
                    ? 'bg-emerald-500 text-white'
                    : theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Contenido */}
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar - Filtros */}
          <aside className={`lg:w-72 ${mostrarFiltros ? 'block' : 'hidden lg:block'} flex-shrink-0 lg:sticky lg:top-[120px] lg:self-start`}>
            <div className={`p-5 rounded-2xl ${theme === 'dark' ? 'bg-gray-800' : 'bg-white shadow-sm border border-gray-100'}`}>
              {/* Header filtros */}
              <div className="flex items-center justify-between mb-5">
                <h3 className={`font-bold flex items-center gap-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  <Filter className="w-5 h-5 text-emerald-500" />
                  Filtros
                </h3>
                {contarActivos > 0 && (
                  <button
                    onClick={reset}
                    className="text-xs text-red-500 hover:text-red-600 font-medium flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    <X className="w-3 h-3" />
                    Limpiar ({contarActivos})
                  </button>
                )}
              </div>

              {/* Categorías */}
              <div className="mb-6">
                <h4 className={`font-semibold mb-3 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Categorías
                </h4>
                <div className="space-y-1.5">
                  {categorias.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setCategoria(cat.id)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-sm transition-all ${
                        categoria === cat.id
                          ? theme === 'dark'
                            ? 'bg-emerald-900/30 text-emerald-400 font-medium'
                            : 'bg-emerald-50 text-emerald-700 font-medium'
                          : theme === 'dark'
                            ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-200'
                            : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {cat.icono ? (
                        <span className={categoria === cat.id ? 'text-emerald-500' : 'text-gray-400'}>
                          {cat.icono}
                        </span>
                      ) : (
                        <span className="w-4" />
                      )}
                      <span>{cat.nombre}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Rango de precio */}
              <div className="mb-6">
                <h4 className={`font-semibold mb-3 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Precio máximo
                </h4>
                <div className={`text-2xl font-bold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  ${precioMax.toLocaleString('es-CO')}
                </div>
                <input
                  type="range"
                  min="0"
                  max="500000"
                  step="5000"
                  value={precioMax}
                  onChange={(e) => setPrecioMax(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-emerald-500"
                />
                <div className="flex justify-between text-xs mt-2 text-gray-500">
                  <span>$0</span>
                  <span>$500.000</span>
                </div>
              </div>

              {/* Disponibilidad */}
              <div>
                <h4 className={`font-semibold mb-3 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Disponibilidad
                </h4>
                <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                  soloDisponibles
                    ? theme === 'dark'
                      ? 'border-emerald-500/50 bg-emerald-900/20'
                      : 'border-emerald-200 bg-emerald-50'
                    : theme === 'dark'
                      ? 'border-gray-700 hover:border-gray-600'
                      : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <input
                    type="checkbox"
                    checked={soloDisponibles}
                    onChange={(e) => setSoloDisponibles(e.target.checked)}
                    className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                  />
                  <div className="flex-1">
                    <span className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                      Solo disponibles
                    </span>
                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                      Ocultar productos agotados
                    </p>
                  </div>
                </label>
              </div>
            </div>
          </aside>

          {/* Productos */}
          <main className="flex-1 min-w-0">
            {/* Mobile filter toggle */}
            <button
              onClick={() => setMostrarFiltros(!mostrarFiltros)}
              className="lg:hidden mb-4 w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-medium flex items-center justify-center gap-2"
            >
              <Filter className="w-5 h-5" />
              <span>{mostrarFiltros ? 'Ocultar filtros' : 'Mostrar filtros'}</span>
              {contarActivos > 0 && (
                <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                  {contarActivos}
                </span>
              )}
            </button>

            {/* Loading */}
            {loading && (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
              </div>
            )}

            {/* Grid / List */}
            {!loading && (
              <>
                {vistaGrid ? (
                  <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {productosFiltrados.slice(0, mostrados).map((producto) => (
                      <ProductCard
                        key={producto.id}
                        producto={producto}
                        theme={theme}
                        added={addedItems.includes(producto.id)}
                        isFavorite={isFavorite(producto.id)}
                        onAddToCart={handleAddToCart}
                        onToggleFavorite={toggleFavorite}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {productosFiltrados.slice(0, mostrados).map((producto) => (
                      <ProductListItem key={producto.id} producto={producto} />
                    ))}
                  </div>
                )}

                {/* Botón Ver Más */}
                {!loading && productosFiltrados.length > mostrados && (
                  <div className="col-span-full flex justify-center mt-6">
                    <button
                      onClick={() => setMostrados(prev => prev + 10)}
                      className="px-6 py-3 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
                    >
                      Ver más ({productosFiltrados.length - mostrados} restantes)
                    </button>
                  </div>
                )}

                {/* Empty state */}
                {productosFiltrados.length === 0 && (
                  <div className={`text-center py-16 rounded-2xl border-2 border-dashed ${
                    theme === 'dark' ? 'border-gray-700 text-gray-400' : 'border-gray-300 text-gray-500'
                  }`}>
                    <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium">No se encontraron productos</p>
                    <p className="mt-1 text-sm">Intenta ajustar los filtros o la búsqueda</p>
                    {contarActivos > 0 && (
                      <button
                        onClick={reset}
                        className="mt-4 px-4 py-2 bg-emerald-500 text-white rounded-lg font-medium hover:bg-emerald-600 transition-colors"
                      >
                        Limpiar filtros
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default ComprasClientePage;
